import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainNavbar from './MainNavbar';

const UsuariosManager = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [temporada, setTemporada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nombre: '',
    role: 'entrenador',
    categoria_id: ''
  });
  const [confirmReset, setConfirmReset] = useState(false);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate('/admin');
        return;
      }

      const [resUsuarios, resCat, resTemp] = await Promise.all([
        fetch('/api/usuarios', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/categorias'),
        fetch('/api/temporada', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (resUsuarios.status === 401 || resUsuarios.status === 403) {
        localStorage.removeItem('token');
        navigate('/admin');
        return;
      }

      setUsuarios(await resUsuarios.json());
      setCategorias(await resCat.json());
      if (resTemp.ok) setTemporada(await resTemp.json());
    } catch (error) {
      setError('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({ username: '', password: '', nombre: '', role: 'entrenador', categoria_id: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const token = getToken();
      const url = editingId ? `/api/usuarios/${editingId}` : '/api/usuarios';
      const method = editingId ? 'PUT' : 'POST';

      const body = { ...formData };
      if (editingId && !body.password) delete body.password;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const result = await res.json();
      if (res.ok) {
        setMessage(`✓ Usuario ${editingId ? 'actualizado' : 'creado'} correctamente`);
        resetForm();
        fetchData();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(result.error || 'Error guardando usuario');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  const handleEdit = (usuario) => {
    setFormData({
      username: usuario.username,
      password: '',
      nombre: usuario.nombre || '',
      role: usuario.role,
      categoria_id: usuario.categoria_id || ''
    });
    setEditingId(usuario.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try {
      const token = getToken();
      const res = await fetch(`/api/usuarios/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      if (res.ok) {
        setMessage('✓ Usuario eliminado');
        fetchData();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(result.error || 'Error eliminando usuario');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  const exportarArchivo = async () => {
    try {
      const token = getToken();
      const res = await fetch('/api/temporada/exportar', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `archivo_escuela_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage('✓ Archivo descargado con todos los datos');
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      setError('Error exportando datos');
    }
  };

  const nuevaTemporada = async () => {
    try {
      const token = getToken();
      const res = await fetch('/api/temporada/nueva', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ borrar_anteriores: true })
      });
      const result = await res.json();
      if (res.ok) {
        setMessage(`✓ ${result.message}`);
        setConfirmReset(false);
        fetchData();
      } else {
        setError(result.error || 'Error en reset');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <MainNavbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <MainNavbar />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

        {/* Gestión de temporada */}
        {temporada && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">🗓️ Temporada {temporada.actual}</h2>
            <div className="grid grid-cols-2 gap-3 mb-4 text-center">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-blue-700">{temporada.alumnos_actual}</p>
                <p className="text-xs text-gray-600">Alumnos temporada actual</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-700">{temporada.alumnos_otras}</p>
                <p className="text-xs text-gray-600">Alumnos temporadas anteriores</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={exportarArchivo}
                className="flex-1 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
              >
                ⬇ Descargar archivo con todos los datos
              </button>
              <button
                onClick={() => setConfirmReset(true)}
                className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
              >
                🗑 Borrar datos de temporadas anteriores
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Recomendación: descarga primero el archivo (guarda todos los datos) y después borra los datos antiguos para empezar la temporada limpia.
            </p>
          </div>
        )}

        {/* Usuarios y entrenadores */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 sm:p-6 border-b flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">👥 Usuarios y Entrenadores</h2>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
            >
              + Nuevo usuario
            </button>
          </div>

          {message && (
            <div className="mx-4 mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm font-medium">
              {message}
            </div>
          )}
          {error && (
            <div className="mx-4 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {showForm && (
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 border-b bg-gray-50 space-y-3">
              <h3 className="font-semibold text-gray-800">{editingId ? 'Editar usuario' : 'Nuevo usuario'}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Usuario *"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  className="px-3 py-2 border border-gray-300 rounded-md text-base"
                />
                <input
                  type="password"
                  placeholder={editingId ? 'Nueva contraseña (vacío = sin cambio)' : 'Contraseña *'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingId}
                  className="px-3 py-2 border border-gray-300 rounded-md text-base"
                />
                <input
                  type="text"
                  placeholder="Nombre completo"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md text-base"
                />
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md text-base bg-white"
                >
                  <option value="entrenador">Entrenador</option>
                  <option value="admin">Administrador</option>
                </select>
                {formData.role === 'entrenador' && (
                  <select
                    value={formData.categoria_id}
                    onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                    required
                    className="px-3 py-2 border border-gray-300 rounded-md text-base bg-white sm:col-span-2"
                  >
                    <option value="">Asignar grupo/categoría *</option>
                    {categorias.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre} - {c.dias_entreno} {c.hora_inicio}-{c.hora_fin}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium">
                  {editingId ? 'Guardar cambios' : 'Crear usuario'}
                </button>
                <button type="button" onClick={resetForm} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <div className="divide-y">
            {usuarios.map(u => (
              <div key={u.id} className="p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">
                    {u.nombre || u.username}
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {u.role === 'admin' ? 'Admin' : 'Entrenador'}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500">
                    @{u.username}
                    {u.categoria_nombre && ` · Grupo: ${u.categoria_nombre}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(u)}
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-200"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(u.id)}
                    className="px-3 py-1.5 bg-red-100 text-red-700 rounded-md text-xs font-medium hover:bg-red-200"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal confirmación reset temporada */}
      {confirmReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setConfirmReset(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-red-700 mb-3">⚠ Borrar datos de temporadas anteriores</h3>
            <p className="text-sm text-gray-700 mb-2">
              Esto eliminará <strong>permanentemente</strong> los alumnos de temporadas anteriores junto con sus padres, asistencias y observaciones.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              Los alumnos de la temporada actual ({temporada?.actual}) se conservan. ¿Has descargado primero el archivo de respaldo?
            </p>
            <div className="flex gap-3">
              <button
                onClick={exportarArchivo}
                className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
              >
                Descargar primero
              </button>
              <button
                onClick={nuevaTemporada}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
              >
                Sí, borrar antiguos
              </button>
            </div>
            <button onClick={() => setConfirmReset(false)} className="mt-3 w-full py-2 text-gray-500 text-sm hover:text-gray-700">
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosManager;
